import { useEffect, useMemo, useState } from 'react';
import type { Asset } from '@shared/types';
import { AppShell } from '@/components/AppShell/AppShell';
import { useSessionStore } from '@/stores/session';
import { buildAssetList } from '@/lib/assetList';
import { AssetNav, type AssetNavItem } from '@/components/AssetNav/AssetNav';
import { useLeaveGuard } from '@/hooks/useLeaveGuard';
import { completeVersions } from '@/lib/versionStatus';
import { useToast } from '@/components/ToastProvider';
import { deleteCustomAssetWithCleanup } from '@/lib/customAssetOps';
import { getPrimaryVoiceId } from '@/lib/voiceMigration';
import { VersionsBar } from '@/components/VersionsBar/VersionsBar';
import { AssetInfoBox } from '@/components/AssetInfoBox/AssetInfoBox';
import { AssetLengthControl } from '@/components/VideoPlayer/AssetLengthControl';
import { DeleteAssetModal } from '@/components/DeleteAssetModal/DeleteAssetModal';
import { CreateAssetModal } from '@/components/CreateAssetModal/CreateAssetModal';
import {
  WelcomeHowTo,
  hasSeenWelcomeHowTo,
} from '@/components/WelcomeHowTo/WelcomeHowTo';
import { FirstUploadHint } from '@/components/FirstUploadHint/FirstUploadHint';
import { CoachMark } from '@/components/CoachMark/CoachMark';
import { hasSeenFirstUploadHint } from '@/lib/firstUploadHint';
import { TTSView } from './TTSView';
import { STSView } from './STSView';
import { FreeChoiceView } from './FreeChoiceView';
import styles from './index.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';

interface DeleteModalState {
  assetId: string;
  assetTitle: string;
  hasVersions: boolean;
}

const MAX_CUSTOM_ASSETS = 20;

/** Dispatches the generation UI per asset type. */
export function Generation() {
  // Einzelne Slices statt `useSessionStore()` ohne Selector — das abonnierte
  // den ganzen Store und rendete die Seite bei jeder Änderung neu (Audit,
  // Low). `buildAssetList` liefert ein frisches Array, deshalb useMemo.
  const config = useSessionStore((s) => s.config);
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const customAssets = useSessionStore((s) => s.customAssets);
  const assetBlocks = useSessionStore((s) => s.assetBlocks);
  const stsAssets = useSessionStore((s) => s.stsAssets);
  const freeMode = useSessionStore((s) => s.freeMode);
  const assetBusy = useSessionStore((s) => s.assetBusy);
  const versionsMap = useSessionStore((s) => s.versions);
  const sessionUploads = useSessionStore((s) => s.sessionUploads);
  const userName = useSessionStore((s) => s.name);
  const uploadReminder = useSessionStore((s) => s.uploadReminder);
  const setCurrentAssetIdx = useSessionStore((s) => s.setCurrentAssetIdx);
  // Warn on reload / tab close while any asset still lacks an upload.
  useLeaveGuard();
  const toast = useToast();
  const assets = useMemo(
    () => buildAssetList(config?.assets, config?.voices, customAssets),
    [config?.assets, config?.voices, customAssets],
  );
  const predefinedCount = config?.assets.length ?? 0;
  const allowCustomAssets = config?.allowCustomAssets ?? false;
  const simpleMode = config?._simpleMode === true;
  // DeleteAssetModal is mounted at page level (one shared instance) and
  // driven by this state. Open the modal by setting the slot, close by
  // resetting to null. Mirrors how UploadModal is wired in TTSView/STSView.
  const [deleteModal, setDeleteModal] = useState<DeleteModalState | null>(null);
  // CreateAssetModal is opened from the AssetNav "+ New asset" button.
  // Title is collected here and locked in at creation — see
  // handleAddCustomAsset / handleCreateCustomAsset below.
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // First-visit mini how-to — once per browser; the flag is only written
  // when the user clicks "Let's go" inside the modal.
  const [showWelcome, setShowWelcome] = useState(() => !hasSeenWelcomeHowTo());
  // "First version uploaded" popup — once per browser, after the first
  // upload of THIS session (store counter, not the restored version list).
  // Delayed past the UploadModal's 800 ms success checkmark so the two
  // never stack.
  const [showFirstUploadHint, setShowFirstUploadHint] = useState(false);
  useEffect(() => {
    if (sessionUploads !== 1 || hasSeenFirstUploadHint()) return;
    const h = window.setTimeout(() => setShowFirstUploadHint(true), 1000);
    return () => window.clearTimeout(h);
  }, [sessionUploads]);
  // Nach dem „Version uploaded!"-Popup: wippender Pfeil auf das nächste Asset
  // in der Sidebar, verschwindet bei irgendeinem Klick (Philip 2026-10-03,
  // „damit man einen kleinen hinweis auf die sidebar erhält"). Hängt am
  // Popup, hat also dieselbe 7-Tage-Regel; weg auch beim Asset-Wechsel.
  const [showNextAssetHint, setShowNextAssetHint] = useState(false);
  useEffect(() => { setShowNextAssetHint(false); }, [currentAssetIdx]);

  const items: AssetNavItem[] = assets.map((asset, index) => {
    // Nur vollständige Versionen zählen als Upload (Audit M15) — eine halbe
    // (pending) Version darf den grünen Haken nicht auslösen.
    const hasVersion = completeVersions(versionsMap[asset.id]).length > 0;
    // "Ready" mirrors vanilla renderAssetSidebar (legacy lines 3491-3530).
    // The data model diverged: TTS still lives in assetBlocks; STS moved to
    // stsAssets[].master (recording-booth model). Dispatch by (assetType,
    // freeMode) so the readiness signal tracks the data model the active
    // sub-view actually writes to — same pattern as the export-routing
    // fix in commit f449c24. Without this, a free/custom asset that was
    // generated in TTS-mode and then uploaded in STS-mode would leave
    // stale TTS takes in assetBlocks[index] and stay stuck on the yellow
    // "ready" pip post-upload instead of flipping to the green check.
    const isStsMode =
      asset.assetType === 'sts' ||
      (asset.assetType === 'free' && freeMode[index] === 'sts');
    let blocksReady: boolean;
    if (isStsMode) {
      blocksReady = (stsAssets[index]?.master?.length ?? 0) > 0;
    } else {
      // 'tts' and free/custom in TTS-mode — readiness lives in assetBlocks.
      const blocks = assetBlocks[index] ?? [];
      blocksReady =
        blocks.length > 0 &&
        blocks.every((b) => {
          const hasFav = b.favIdx >= 0 && !!b.takes[b.favIdx];
          return hasFav && !!b.text?.trim();
        });
    }
    const hasNewWork = blocksReady && hasVersion;
    // Three-state: uploaded (no new work) > ready (new work or first work) > pending.
    // Spinner override mirrors vanilla `_sidebarSpinners.has(a.idx)` short-
    // circuit at the top of the icon ladder (legacy/index.html line 3546 +
    // 3585) — wins over any other status while an upload/delete is in flight.
    let status: AssetNavItem['status'] =
      hasVersion && !hasNewWork
        ? 'uploaded'
        : blocksReady
          ? 'ready'
          : 'pending';
    if (assetBusy[index]) status = 'spinner';
    return {
      asset,
      index,
      status,
      isCustom: index >= predefinedCount,
      // Für den Fortschrittsbalken zählt die Version, nicht der Icon-Status:
      // neue Arbeit nach dem Upload nimmt ein geliefertes Asset nicht zurück.
      delivered: hasVersion,
    };
  });

  const activeAsset = assets[currentAssetIdx];
  const assetType = activeAsset?.assetType ?? 'tts';
  const activeVoiceName = activeAsset
    ? config?.voices.find((v) => v.id === getPrimaryVoiceId(activeAsset))?.name
    : undefined;

  // "+ New asset" button just opens the modal — the actual asset is
  // created in handleCreateCustomAsset below once the user submits a
  // title. Locking the title at creation kills the WebDAV folder
  // mismatch caused by post-upload renames.
  const handleAddCustomAsset = () => {
    const customCount = customAssets.length;
    if (customCount >= MAX_CUSTOM_ASSETS) return;
    setCreateModalOpen(true);
  };

  const handleCreateCustomAsset = (title: string) => {
    const customCount = customAssets.length;
    if (customCount >= MAX_CUSTOM_ASSETS) return;
    const store = useSessionStore.getState();
    const newAsset: Asset = {
      id: `custom-${Date.now()}`,
      assetType: 'free',
      title,
      script: '',
      // Per-mode editable scripts (see Asset type) so toggling TTS/STS
      // doesn't clobber the talent's work in the other mode.
      scriptTts: '',
      scriptSts: '',
      voiceIds: [],
      targetDuration: null,
    };
    store.upsertCustomAsset(newAsset);
    store.setCurrentAssetIdx(predefinedCount + customCount);
    setCreateModalOpen(false);
  };

  const handleRequestDeleteCustomAsset = (assetId: string) => {
    // The "is anything worth losing?" gate (vanilla legacy/index.html:3714-3727).
    // For empty new assets we delete immediately without prompting; for any
    // populated asset we open the modal so the user picks local-vs-cloud.
    const idx = assets.findIndex((a) => a.id === assetId);
    if (idx < 0) return;
    const store = useSessionStore.getState();
    const target = assets[idx];
    const versions = store.versions[assetId] ?? [];
    const hasVersions = versions.length > 0;
    const blocks = store.assetBlocks[idx] ?? [];
    const stsState = store.stsAssets[idx];
    const hasContent =
      !!target.title?.trim() ||
      !!target.script?.trim() ||
      !!target.scriptTts?.trim() ||
      !!target.scriptSts?.trim() ||
      (target.voiceIds && target.voiceIds.length > 0) ||
      !!target.voiceId ||
      hasVersions ||
      blocks.some((b) => !!b.text?.trim() || b.takes.length > 0) ||
      (stsState?.takes?.length ?? 0) > 0 ||
      (stsState?.master?.length ?? 0) > 0;

    if (!hasContent) {
      // Empty asset slot — drop without prompting (matches vanilla).
      void runDelete(assetId, idx, /*skipNetwork*/ true);
      return;
    }

    setDeleteModal({
      assetId,
      assetTitle: target.title || 'Untitled',
      hasVersions,
    });
  };

  const runDelete = async (
    assetId: string,
    idx: number,
    skipNetwork: boolean,
  ) => {
    const store = useSessionStore.getState();
    store.setAssetBusy(idx, true);
    try {
      const result = await deleteCustomAssetWithCleanup(assetId, {
        skipNetwork,
      });
      if (!result.ok) {
        toast.show(result.error || 'Could not delete asset', 4000);
      }
    } finally {
      store.setAssetBusy(idx, false);
    }
  };

  const handleConfirmDelete = ({ deleteCloud }: { deleteCloud: boolean }) => {
    if (!deleteModal) return;
    const { assetId, assetTitle } = deleteModal;
    // Final browser-native safety prompt before the delete actually fires
    // (FB — 2026-04-28). Wording mirrors the modal's copy: cloud-delete
    // gets the louder "AND remove all uploaded files at TRO" warning,
    // local-only gets the milder phrasing. On cancel we deliberately
    // leave the modal mounted so the user can reconsider their choice
    // (toggle the checkbox) or click Cancel themselves to back out.
    const ok = window.confirm(
      deleteCloud
        ? `Really delete "${assetTitle}" locally AND remove all uploaded files at TRO?\n\nThis cannot be undone.`
        : `Really delete "${assetTitle}" locally?`,
    );
    if (!ok) return;
    const idx = assets.findIndex((a) => a.id === assetId);
    setDeleteModal(null);
    if (idx < 0) return;
    // skipNetwork is the negation of "user opted into cloud cleanup".
    // hasVersions is already implicit in the modal — the checkbox isn't
    // shown when versions are empty, so deleteCloud=true here always
    // means there's something to clean up. See customAssetOps.ts header
    // for the orphan-folder trade-off when skipNetwork is true.
    void runDelete(assetId, idx, /*skipNetwork*/ !deleteCloud);
  };

  const sidebar = (
    <AssetNav
      items={items}
      activeIndex={currentAssetIdx}
      onSelect={(idx) => setCurrentAssetIdx(idx)}
      allowCustomAssets={allowCustomAssets}
      customAssetCount={customAssets.length}
      maxCustomAssets={MAX_CUSTOM_ASSETS}
      onAddCustomAsset={handleAddCustomAsset}
      onDeleteCustomAsset={handleRequestDeleteCustomAsset}
      remindUnuploaded={uploadReminder}
    />
  );

  const hasTitle = !!activeAsset?.title?.trim();
  const isFreeOrCustom = activeAsset?.assetType === 'free';
  // Custom assets render their title (now static — captured at creation by
  // CreateAssetModal) inside FreeAssetHeader so VersionsBar / AssetLength
  // can sit in the same vertical slot they do for predefined assets.
  // Suppress the page-level h1 here to avoid a duplicate title.
  const isCustom = currentAssetIdx >= predefinedCount;

  return (
    <AppShell contentMode="split" sidebar={sidebar}>
      <main className={styles.main}>
        {(hasTitle || !isFreeOrCustom) && !isCustom && (
          <h1 className={styles.title}>
            <span className={styles.titleText} {...scriptAttrs(activeAsset?.title)}>
              {activeAsset?.title || 'No asset selected'}
            </span>
            {activeVoiceName && (
              <span className={styles.titleVoice}>{activeVoiceName}</span>
            )}
          </h1>
        )}

        <AssetInfoBox text={activeAsset?.info} />

        {/* VersionsBar mounts in a single canonical place directly under
            the title row (FB3#2 — 2026-04-28). Predefined assets get the
            page-level mount here, directly under the h1. Custom assets
            instead receive the bar via FreeChoiceView → FreeAssetHeader's
            `versionsBarSlot` so it sits between the static title row and
            the voice/length sections (FB4 — 2026-04-28). Without this
            routing the page-level bar would render ABOVE FreeAssetHeader's
            title, since FreeAssetHeader is the very first thing
            FreeChoiceView returns. */}
        {activeAsset && !isCustom && <VersionsBar />}

        {/* AssetLengthControl mounts here for non-custom assets so it sits
            in the same position the custom-asset header places it (FB3#7 —
            2026-04-28). Custom assets keep their own controlled instance
            inside FreeAssetHeader (under the voice picker), so we suppress
            the page-level mount when isCustom to avoid a double render.
            The component still self-hides when its asset has video / admin
            duration via its internal `effectiveAssetType` gate. */}
        {/* simpleMode (Multi-Lingual): die Timeline ist flexibel, es gibt
            keine Asset-Länge mehr zu setzen (Spec 2026-09-14). */}
        {activeAsset && !isCustom && !simpleMode && <AssetLengthControl />}

        {activeAsset && assetType === 'tts' && <TTSView />}
        {activeAsset && assetType === 'sts' && <STSView />}
        {activeAsset && assetType === 'free' && (
          <FreeChoiceView
            versionsBarSlot={isCustom ? <VersionsBar /> : undefined}
          />
        )}
      </main>

      <DeleteAssetModal
        open={deleteModal !== null}
        assetTitle={deleteModal?.assetTitle ?? ''}
        hasVersions={deleteModal?.hasVersions ?? false}
        onCancel={() => setDeleteModal(null)}
        onConfirm={handleConfirmDelete}
      />

      <CreateAssetModal
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onCreate={handleCreateCustomAsset}
      />

      {showWelcome && (
        <WelcomeHowTo name={userName} onDismiss={() => setShowWelcome(false)} />
      )}
      {showFirstUploadHint && (
        <FirstUploadHint
          onDismiss={() => {
            setShowFirstUploadHint(false);
            if (currentAssetIdx + 1 < assets.length) setShowNextAssetHint(true);
          }}
        />
      )}
      {showNextAssetHint && currentAssetIdx + 1 < assets.length && (
        <CoachMark
          variant="arrow"
          placement="right"
          attach="fixed"
          targets={[`asset-${currentAssetIdx + 1}`]}
          dismissOnAnyClick
          onDismiss={() => setShowNextAssetHint(false)}
        />
      )}
    </AppShell>
  );
}
